import {keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

import {useToast} from "@/hooks/use-toast";
import * as apiClient from "@/lib/api";
import {ApiError} from "@/lib/api";
import {ROLES} from "@/lib/auth/roles";
import useIsQueryEnabled from "@/lib/hooks/query-enabler";
import {useQueryResetWithRefresh} from "@/lib/hooks/query-refetch-with-reset";
import {
  OpportunityFormValuesType,
  RiskAndOpportunitySettings,
  RiskFormValuesType,
  UpdateRiskAndOpportunity
} from "@/modules/risk-and-opportunity-settings/types";
import {useToken} from "@/stores/token";
import {
  QUERY_KEY_OPPORTUNITY_SETTINGS_FETCH,
  QUERY_KEY_RISK_SETTINGS_FETCH
} from "@/utils/keys";
import {OPPORTUNITY_SETTINGS_API_URL, RISK_SETTINGS_API_URL} from "@/utils/urls";

function fetchRiskSettings(): Promise<RiskAndOpportunitySettings[]> {
  return apiClient.get(RISK_SETTINGS_API_URL)
}

export const riskSettingsQueryOptions = () =>
  queryOptions({
    queryKey: [QUERY_KEY_RISK_SETTINGS_FETCH],
    queryFn: fetchRiskSettings,
    placeholderData: keepPreviousData
  });

export const useRiskSettings = () => {
  const { token } = useToken()

  const queryOptions = riskSettingsQueryOptions()
  queryOptions.enabled = useIsQueryEnabled(!!token?.roles.includes(ROLES.ROLE_ELEVATION))

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchOpportunitySettings(): Promise<RiskAndOpportunitySettings[]> {
  return apiClient.get(OPPORTUNITY_SETTINGS_API_URL)
}

export const opportunitySettingsQueryOptions = () =>
  queryOptions({
    queryKey: [QUERY_KEY_OPPORTUNITY_SETTINGS_FETCH],
    queryFn: fetchOpportunitySettings,
    placeholderData: keepPreviousData
  });

export const useOpportunitySettings = () => {
  const { token } = useToken()

  const queryOptions = opportunitySettingsQueryOptions()
  queryOptions.enabled = useIsQueryEnabled(!!token?.roles.includes(ROLES.ROLE_ELEVATION))

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

export function updateOpportunitySettings(params: UpdateRiskAndOpportunity[]) {
  const filter = JSON.stringify({
    id: params.map(p => p.id)
  })

  const url = `${OPPORTUNITY_SETTINGS_API_URL}?filter=${encodeURIComponent(filter)}`
  return apiClient.put(url, {data: params})
}

export const useOpportunitySettingsMutation = (onSettled: () => void) => {
  const queryClient = useQueryClient();
  const {toast} = useToast()

  return useMutation({
    mutationFn: async (values: OpportunityFormValuesType) => {
      const formattedValues: UpdateRiskAndOpportunity[] = Object.entries(values.rows).map(
        ([id, row]) => ({
          id: Number(id),
          visible: row.visible ?? false
        })
      )
      return updateOpportunitySettings(formattedValues)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_OPPORTUNITY_SETTINGS_FETCH]});
      toast({description: 'Changes saved.', variant: 'mint'})
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.message) {
        toast({description: e.message, variant: 'red'})
      } else {
        toast({description: 'Error updating settings, please try again'})
      }
    },
    onSettled: onSettled
  })
}


export function updateRiskSettings(params: UpdateRiskAndOpportunity[]) {
  const filter = JSON.stringify({
    id: params.map(p => p.id)
  })

  const url = `${RISK_SETTINGS_API_URL}?filter=${encodeURIComponent(filter)}`
  return apiClient.put(url, {data: params})
}

export const useRiskSettingsMutation = (onSettled: () => void) => {
  const queryClient = useQueryClient();
  const {toast} = useToast()

  return useMutation({
    mutationFn: async (values: RiskFormValuesType) => {
      const formattedValues: UpdateRiskAndOpportunity[] = Object.entries(values.rows).map(
        ([id, row]) => ({
          id: Number(id),
          threshold: row.threshold ?? null,
          visible: row.visible ?? false
        })
      )
      return updateRiskSettings(formattedValues)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_RISK_SETTINGS_FETCH]});
      toast({description: 'Changes saved.', variant: 'mint'})
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.message) {
        toast({description: e.message, variant: 'red'})
      } else {
        toast({description: 'Error updating settings, please try again'})
      }
    },
    onSettled: onSettled
  })
}


